/**
 * globalLeadFilters.js — Shared lead-filter state across Leads, Contacts,
 * and Demo Bookings pages.
 *
 * Iter 138 / ioannis@ehiehr.com bug fix: previously the Leads page filters
 * lived ONLY in the URL — so navigating to Contacts or Bookings wiped them,
 * and those pages had no way to apply the same lead-filter sidebar.
 *
 * This module gives a single source of truth backed by `localStorage` so:
 *   - Leads filters survive away+back navigation (URL still drives /leads,
 *     localStorage just mirrors so re-entry from any other route restores).
 *   - Contacts and Bookings open the SAME `<LeadFilterPanel>` sidebar and
 *     filter their data by looking up the underlying lead.
 *   - Cross-tab sync via a `storage` event listener so two open windows
 *     stay in lockstep.
 */
import { useEffect, useState, useCallback } from "react";
import { EMPTY_LEAD_FILTERS } from "@/components/LeadFilterPanel";
import { EMPTY_EXTRA_FILTERS } from "@/lib/leadFilterConfig";

const STORAGE_KEY = "eh.globalLeadFilters.v1";
const CHANGE_EVENT = "eh:global-lead-filters:change";

function _read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { filters: { ...EMPTY_LEAD_FILTERS }, extra: { ...EMPTY_EXTRA_FILTERS } };
    const parsed = JSON.parse(raw);
    return {
      filters: { ...EMPTY_LEAD_FILTERS, ...(parsed.filters || {}) },
      extra: { ...EMPTY_EXTRA_FILTERS, ...(parsed.extra || {}) },
    };
  } catch {
    return { filters: { ...EMPTY_LEAD_FILTERS }, extra: { ...EMPTY_EXTRA_FILTERS } };
  }
}

function _write(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* quota */ }
  try { window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: state })); } catch { /* ignore */ }
}

export function getGlobalLeadFilters() { return _read(); }

export function setGlobalLeadFilters(filters, extra) {
  _write({ filters: { ...EMPTY_LEAD_FILTERS, ...filters }, extra: { ...EMPTY_EXTRA_FILTERS, ...extra } });
}

export function clearGlobalLeadFilters() {
  _write({ filters: { ...EMPTY_LEAD_FILTERS }, extra: { ...EMPTY_EXTRA_FILTERS } });
}

/**
 * React hook — keeps a component in sync with the global state.
 * Returns `[filters, setFilters, extra, setExtra, count]`.
 */
export function useGlobalLeadFilters() {
  const [state, setState] = useState(() => _read());

  useEffect(() => {
    const onChange = (ev) => {
      const next = ev?.detail || _read();
      setState({
        filters: { ...EMPTY_LEAD_FILTERS, ...(next.filters || {}) },
        extra: { ...EMPTY_EXTRA_FILTERS, ...(next.extra || {}) },
      });
    };
    const onStorage = (ev) => {
      if (ev.key === STORAGE_KEY) setState(_read());
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setFilters = useCallback((next) => {
    const resolved = typeof next === "function" ? next(state.filters) : next;
    _write({ filters: { ...EMPTY_LEAD_FILTERS, ...resolved }, extra: state.extra });
  }, [state]);

  const setExtra = useCallback((next) => {
    const resolved = typeof next === "function" ? next(state.extra) : next;
    _write({ filters: state.filters, extra: { ...EMPTY_EXTRA_FILTERS, ...resolved } });
  }, [state]);

  // Count: sum of array lengths in filters + 1 for non-default lastContact.
  const count = Object.values(state.filters).reduce(
    (sum, v) => sum + (Array.isArray(v) ? v.length : 0),
    0,
  ) + (state.extra.lastContact && state.extra.lastContact !== "any" ? 1 : 0)
    + (Array.isArray(state.extra.state) ? state.extra.state.length : 0)
    + (Array.isArray(state.extra.city) ? state.extra.city.length : 0)
    + (Array.isArray(state.extra.zip) ? state.extra.zip.length : 0);

  return [state.filters, setFilters, state.extra, setExtra, count];
}
